import { CheckIcon, TriangleAlert, User as UserIcon } from "lucide-react";
import * as React from "react";

import { Avatar, DropdownMenu, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { extractInitials } from "@/utils";
import { User, VideoProject } from "@/services/bedrock";

import {
  assignableEditorUsers,
  confirmOutsideAssignedStaffAssignment,
  confirmSamePersonEditorQaAssignment,
  isNewSamePersonEditorQaAssignment,
  isSamePersonEditorQaAssignment,
  otherStaffRole,
  resolveStaffUser,
  sortStaffWithCurrentUserFirst,
  staffDisplayName,
  staffRoleLabel,
} from "./staff";

export {
  assignedUserIdForRole,
  assigningRoleForEvent,
  canUnassignStaff,
  confirmAssignSelfOnTransition,
  confirmEventSamePersonAssignment,
  recommendedStaffForRole,
  resolveStaffActorUserId,
  resolveStaffUser,
  staffDisplayName,
  staffRoleLabel,
} from "./staff";

interface VideoProjectStaffPickerProps {
  role: VideoProject.QueueAssignment["role"];
  selectedUserId?: number | null;
  selectedUser?: User;
  otherRoleUserId?: number | null;
  recommendedUsers: User[];
  staffUsers: User[];
  currentUserId?: number;
  compact?: boolean;
  labeled?: boolean;
  disabled?: boolean;
  allowUnassign?: boolean;
  onSelect: (userId: number | null) => void;
}

function stopPointer(event: { stopPropagation(): void }) {
  event.stopPropagation();
}

const StaffAvatar = React.forwardRef<
  HTMLSpanElement,
  {
    user?: User;
    role?: VideoProject.QueueAssignment["role"];
    variant?: "assigned" | "suggested" | "empty";
  } & React.ComponentPropsWithoutRef<typeof Avatar>
>(function StaffAvatar({ user, role, variant = "assigned", className, ...props }, ref) {
  const name = user ? staffDisplayName(user) : "Unassigned";
  const initials = user ? extractInitials(name) : "";
  const showPlaceholder = variant === "empty" || !user;
  const photoUrl = showPlaceholder ? undefined : user?.photoUrl || undefined;
  const [photoFailed, setPhotoFailed] = React.useState(false);
  const washed = variant !== "assigned" && !showPlaceholder;
  const washClassName =
    washed &&
    "opacity-50 transition-opacity duration-150 hover:opacity-100 group-hover:opacity-100 group-hover/avatar:opacity-100 group-hover/dropdown-menu-item:opacity-100 group-data-[highlighted]/dropdown-menu-item:opacity-100";

  React.useLayoutEffect(() => {
    setPhotoFailed(false);
  }, [photoUrl]);

  const showPhoto = Boolean(photoUrl) && !photoFailed;

  return (
    <Avatar
      key={user?.id ?? `empty-${role ?? "none"}`}
      ref={ref}
      size="sm"
      className={cn(
        "bg-background after:hidden transition-shadow",
        showPlaceholder ? "shadow-none hover:shadow-sm" : "shadow-sm",
        className
      )}
      {...props}
    >
      {showPhoto ? (
        <img
          data-slot="avatar-image"
          src={photoUrl}
          alt={name}
          decoding="sync"
          className={cn("aspect-square size-full rounded-full object-cover", washClassName)}
          onError={() => setPhotoFailed(true)}
        />
      ) : (
        <Avatar.Fallback
          className={cn(
            "text-[9px] font-medium",
            showPlaceholder ? "bg-muted text-muted-foreground" : "bg-sky-600 text-white",
            washClassName
          )}
        >
          {showPlaceholder ? <UserIcon className="size-4" /> : initials}
        </Avatar.Fallback>
      )}
    </Avatar>
  );
});

export function VideoProjectStaffPicker({
  role,
  selectedUserId,
  selectedUser,
  otherRoleUserId,
  recommendedUsers,
  staffUsers,
  currentUserId,
  compact = false,
  labeled = false,
  disabled = false,
  allowUnassign = true,
  onSelect,
}: VideoProjectStaffPickerProps) {
  const label = staffRoleLabel(role);
  const otherLabel = staffRoleLabel(otherStaffRole(role));
  const currentUser = resolveStaffUser(currentUserId, staffUsers);
  const showAssignToMe = currentUserId != null && currentUser != null;
  const orderedRecommended = React.useMemo(
    () => sortStaffWithCurrentUserFirst(recommendedUsers, currentUserId),
    [currentUserId, recommendedUsers]
  );
  const isCurrentUserRecommended = React.useMemo(
    () => currentUserId != null && orderedRecommended.some(user => user.id === currentUserId),
    [currentUserId, orderedRecommended]
  );
  const assignableStaff = React.useMemo(() => assignableEditorUsers(staffUsers), [staffUsers]);
  const assignableIds = React.useMemo(() => new Set(assignableStaff.map(user => user.id)), [assignableStaff]);
  const dropdownRecommended = React.useMemo(
    () =>
      orderedRecommended.filter(user => {
        if (user.id === currentUserId || user.id === selectedUserId) return true;
        return assignableIds.has(user.id);
      }),
    [assignableIds, currentUserId, orderedRecommended, selectedUserId]
  );
  const recommendedIds = React.useMemo(() => new Set(orderedRecommended.map(user => user.id)), [orderedRecommended]);
  const everyone = React.useMemo(
    () =>
      assignableStaff.filter(
        user => !recommendedIds.has(user.id) && user.id !== currentUserId && user.id !== selectedUserId
      ),
    [assignableStaff, currentUserId, recommendedIds, selectedUserId]
  );

  const isSelectedUnassigned = selectedUserId == null;
  const isSelectedCurrentUser = currentUserId != null && selectedUserId === currentUserId;
  const isSelectedRecommended = selectedUserId != null && recommendedIds.has(selectedUserId);
  const assignToMeAtTop = showAssignToMe && !isCurrentUserRecommended && !isSelectedCurrentUser;
  const showUnassignedAction = allowUnassign && !isSelectedUnassigned;

  const triggerLabel = selectedUser
    ? `${label}: ${isSelectedCurrentUser ? `${staffDisplayName(selectedUser)} (you)` : staffDisplayName(selectedUser)}`
    : `Choose ${label}`;

  const triggerClassName = "cursor-pointer rounded-full outline-none transition-transform hover:scale-110";

  const menuItemClassName =
    "gap-2 py-1.5 [&_[data-slot=avatar]_[data-slot=avatar-fallback]]:!text-white focus:[&_[data-slot=avatar]_[data-slot=avatar-fallback]]:!text-white data-highlighted:[&_[data-slot=avatar]_[data-slot=avatar-fallback]]:!text-white [&_[data-slot=avatar]_[data-slot=avatar-fallback]_svg]:!text-current focus:[&_[data-slot=avatar]_[data-slot=avatar-fallback]_svg]:!text-current data-highlighted:[&_[data-slot=avatar]_[data-slot=avatar-fallback]_svg]:!text-current";

  const renderConflictIcon = () => (
    <TriangleAlert className="ml-auto size-3.5 text-amber-500" aria-label={`Already assigned as ${otherLabel}`} />
  );

  const [open, setOpen] = React.useState(false);
  const canAssign = !disabled;

  React.useEffect(() => {
    if (!canAssign) setOpen(false);
  }, [canAssign]);

  const handleSelect = async (userId: number | null) => {
    setOpen(false);
    if (userId != null && userId !== selectedUserId) {
      const confirmed = await confirmOutsideAssignedStaffAssignment({
        userId,
        assignedUsers: recommendedUsers,
        assigningRole: role,
        staffUsers,
        currentUserId,
      });
      if (!confirmed) return;
    }
    if (isNewSamePersonEditorQaAssignment(userId, otherRoleUserId, selectedUserId)) {
      const user = resolveStaffUser(userId, staffUsers);
      const confirmed = await confirmSamePersonEditorQaAssignment({
        assigningRole: role,
        userName: user ? staffDisplayName(user) : undefined,
        isCurrentUser: currentUserId != null && userId === currentUserId,
      });
      if (!confirmed) return;
    }
    onSelect(userId);
  };

  const renderUserItem = (user: User) => {
    const selected = user.id === selectedUserId;
    const isCurrentUser = currentUserId != null && user.id === currentUserId;
    const conflictsWithOtherRole = isSamePersonEditorQaAssignment(user.id, otherRoleUserId);
    return (
      <DropdownMenu.Item key={user.id} className={menuItemClassName} onClick={() => void handleSelect(user.id)}>
        <StaffAvatar user={user} role={role} variant={selected ? "assigned" : "suggested"} />
        <span className={cn("flex min-w-0 items-baseline gap-1", selected && "font-bold")}>
          <span className="truncate">{staffDisplayName(user)}</span>
          {isCurrentUser ? <span className="italic shrink-0 font-normal">(you)</span> : null}
        </span>
        {selected ? <CheckIcon className="ml-auto size-3.5" /> : null}
        {!selected && conflictsWithOtherRole ? renderConflictIcon() : null}
      </DropdownMenu.Item>
    );
  };

  const renderUnassignedItem = () => (
    <DropdownMenu.Item key="unassigned" className={menuItemClassName} onClick={() => void handleSelect(null)}>
      <span className={cn("italic", isSelectedUnassigned && "font-bold")}>Unassigned</span>
      {isSelectedUnassigned ? <CheckIcon className="ml-auto size-3.5" /> : null}
    </DropdownMenu.Item>
  );

  const renderAssignToMeItem = () => (
    <DropdownMenu.Item
      key="assign-to-me"
      className={menuItemClassName}
      onClick={() => currentUserId != null && void handleSelect(currentUserId)}
    >
      <StaffAvatar user={currentUser} role={role} variant={isSelectedCurrentUser ? "assigned" : "suggested"} />
      <span className={cn("flex min-w-0 items-baseline gap-1", isSelectedCurrentUser && "font-bold")}>
        <span className="truncate">{currentUser ? staffDisplayName(currentUser) : ""}</span>
        <span className="italic shrink-0 font-normal">(you)</span>
      </span>
      {isSelectedCurrentUser ? <CheckIcon className="ml-auto size-3.5" /> : null}
      {!isSelectedCurrentUser && isSamePersonEditorQaAssignment(currentUserId, otherRoleUserId)
        ? renderConflictIcon()
        : null}
    </DropdownMenu.Item>
  );

  const selectedTopItem = isSelectedRecommended
    ? null
    : isSelectedUnassigned
    ? renderUnassignedItem()
    : isSelectedCurrentUser
    ? renderAssignToMeItem()
    : selectedUser
    ? renderUserItem(selectedUser)
    : null;

  const hasRecommended = dropdownRecommended.length > 0;
  const hasEveryone = everyone.length > 0;
  const hasStaffLists = hasRecommended || hasEveryone;

  const menuContent = (
    <DropdownMenu.Content align={compact ? "end" : "start"} className="min-w-52">
      {selectedTopItem}
      {assignToMeAtTop ? renderAssignToMeItem() : null}
      {(selectedTopItem || assignToMeAtTop) && (showUnassignedAction || hasStaffLists) ? (
        <DropdownMenu.Separator />
      ) : null}
      {showUnassignedAction ? renderUnassignedItem() : null}
      {showUnassignedAction && hasStaffLists ? <DropdownMenu.Separator /> : null}
      {hasRecommended ? (
        <DropdownMenu.Group>
          <DropdownMenu.Label>Recommended</DropdownMenu.Label>
          {dropdownRecommended.map(user => renderUserItem(user))}
        </DropdownMenu.Group>
      ) : null}
      {hasRecommended && hasEveryone ? <DropdownMenu.Separator /> : null}
      {hasEveryone ? everyone.map(user => renderUserItem(user)) : null}
    </DropdownMenu.Content>
  );

  const selectedName = selectedUser
    ? `${staffDisplayName(selectedUser)}${isSelectedCurrentUser ? " (you)" : ""}`
    : "Unassigned";

  const trigger = (
    <Tooltip>
      <DropdownMenu open={open} onOpenChange={nextOpen => canAssign && setOpen(nextOpen)}>
        <DropdownMenu.Trigger
          nativeButton={false}
          disabled={!canAssign}
          aria-label={triggerLabel}
          render={({ className, ref, children: _children, ...triggerProps }) => {
            const sharedClassName = typeof className === "string" ? className : undefined;

            return (
              <Tooltip.Trigger
                render={
                  labeled ? (
                    <span
                      {...triggerProps}
                      ref={ref}
                      className={cn(
                        "inline-flex items-center gap-2 rounded-md outline-none",
                        canAssign ? "cursor-pointer" : "cursor-default",
                        sharedClassName
                      )}
                    >
                      <StaffAvatar user={selectedUser} role={role} variant={selectedUser ? "assigned" : "empty"} />
                      <span
                        className={cn("text-sm font-medium", selectedUser ? "text-slate-700" : "text-slate-400 italic")}
                      >
                        {selectedName}
                      </span>
                    </span>
                  ) : (
                    <StaffAvatar
                      {...triggerProps}
                      ref={ref}
                      user={selectedUser}
                      role={role}
                      variant={selectedUser ? "assigned" : "empty"}
                      className={cn(triggerClassName, !canAssign && "cursor-default hover:scale-100", sharedClassName)}
                    />
                  )
                }
              />
            );
          }}
        />
        {menuContent}
      </DropdownMenu>
      {labeled ? null : <Tooltip.Content>{triggerLabel}</Tooltip.Content>}
    </Tooltip>
  );

  return (
    <div
      data-kanban-interactive=""
      className={cn("pointer-events-auto flex min-w-0 items-center", compact && "shrink-0")}
      onPointerDown={stopPointer}
      onMouseDown={stopPointer}
      onClick={stopPointer}
    >
      {trigger}
    </div>
  );
}
